import { deepMerge } from "./deep-merge";
/**
 * Singleton-scoped registry. Owns:
 *   - Domain manifests by id,
 *   - Raw part definitions by id (as authored),
 *   - Resolved part prototypes (after `extends` deep-merge),
 *   - Component classes registered by domains,
 *   - System classes registered by domains.
 *
 * The runtime loader fills these tables at boot. The Catalog reads parts;
 * the World's Scheduler reads systems; the LathePart renderer reads parts
 * and uses Component classes to instantiate per-entity state.
 */
export class Registry {
    domains = new Map();
    rawParts = new Map();
    resolvedCache = new Map();
    componentClasses = new Map();
    systemClasses = [];
    registerDomain(m) {
        if (this.domains.has(m.id)) {
            console.warn(`[registry] duplicate domain id: ${m.id}`);
        }
        this.domains.set(m.id, m);
    }
    registerPart(p) {
        if (this.rawParts.has(p.id)) {
            console.warn(`[registry] duplicate part id: ${p.id}`);
        }
        this.rawParts.set(p.id, p);
    }
    registerComponent(cls) {
        const type = cls.type;
        if (!type)
            throw new Error(`component ${cls.name} missing static type`);
        if (this.componentClasses.has(type)) {
            console.warn(`[registry] duplicate component type: ${type}`);
        }
        this.componentClasses.set(type, cls);
    }
    registerSystem(cls) {
        this.systemClasses.push(cls);
    }
    getDomain(id) {
        return this.domains.get(id);
    }
    hasPart(id) {
        return this.rawParts.has(id);
    }
    getComponentClass(type) {
        return this.componentClasses.get(type);
    }
    getSystemClasses() {
        return this.systemClasses;
    }
    /** Return a part with `extends` chain fully merged. Cached. */
    resolvePart(id) {
        const cached = this.resolvedCache.get(id);
        if (cached)
            return cached;
        const merged = this.resolveChain(id, new Set());
        this.resolvedCache.set(id, merged);
        return merged;
    }
    resolveChain(id, seen) {
        if (seen.has(id)) {
            throw new Error(`extends cycle: ${[...seen, id].join(" -> ")}`);
        }
        const own = this.rawParts.get(id);
        if (!own)
            throw new Error(`part not registered: ${id}`);
        if (!own.extends)
            return structuredClone(own);
        seen.add(id);
        const parent = this.resolveChain(own.extends, seen);
        return deepMerge(parent, own);
    }
    allParts() {
        return Array.from(this.rawParts.values()).map((p) => this.resolvePart(p.id));
    }
    catalogParts() {
        return this.allParts().filter((p) => p.category !== "_base");
    }
    allDomains() {
        return Array.from(this.domains.values());
    }
}
export const registry = new Registry();
